/* =============================================================================
   ics-window - the ESimSol window component (stylesheet:
   lib-v2\css\components\ics-window.css).

   ONE module, two jQuery names, one stack:
   - $.fn.window     the widget bridge (generated by the facade from the Registry
                     entry below): acts on ITS OWN element(s) - open / close /
                     center / setTitle / resize, or an options object to
                     (re)configure.
   - $.fn.icsWindow  the application-level open/close call every screen uses:
                     'open' acts on the receiver (and applies a title); 'close'
                     closes the topmost OPEN window AMONG THE RECEIVER'S elements
                     ($('#win').icsWindow('close') closes #win), and when the
                     receiver names no open window ($('div'), $(this) of a
                     button, an already-closed id) the topmost window overall.
   - WindowStackService is the single LIFO of open windows: every open pushes,
     every close pops (close button, direct close, ESC, destroy). Escape closes
     the topmost window through the stack (KeyService, installed by the shell) -
     this module binds NO key handler of its own.
   - A window opened with `box: true` (the messager's message boxes and toasts)
     is on the stack for z-order only: no close-topmost path (icsWindow('close'),
     IcsWindowClose, Escape dispatch) ever targets it - it answers only through
     its own buttons/keys. So `IcsConfirm(...)` or `IcsToastOk(...)` followed by
     `$('#win').icsWindow('close')` closes #win, not the box.

   A dialog: brand title bar with the title and ONE close button (the
   close-cancel ics-icon), a dimmer when modal, arrives with a short fade-and-
   rise, draggable by its title bar, resizable at its edges - both switchable
   per window. The component instance lives on the SOURCE div (all APIs target
   '#srcId'). At mount the source div becomes the window BODY inside a
   generated wrapper that is relocated to document.body:

     <div class="ics-window">                      wrapper (positioned, z from LayoutService)
       <div class="ics-window__header">            title bar - drag handle
         <span class="ics-window__title">          raw HTML title
         <a class="ics-window__close">             the only tool: <i class="ics-icon-close-cancel">
       <div class="ics-window__bodysrc">           = the source div (id, padding, content untouched)
       <span class="ics-window__rs ics-window__rs--n|s|e|w|ne|nw|se|sw">  resize handles
     <div class="ics-window__mask">                per-window dimmer (modal only, z-1)

   Page code that removes a source div (`$('#win').remove()` before re-creating
   it) leaves the wrapper + mask behind; every later mount sweeps such orphans
   (also callable: Window.sweepOrphans()).

   Also here: icsWindowCloseTop / IcsWindowClose (close the topmost window),
   two window-adjacent page globals - UnselectAllRowsOfATable (a no-op that
   exists for its name) and OpenWindowWithPost - attached with the family, and
   installPanelDispatch($), which lets a window answer the panel vocabulary
   (`$('#win').panel('setTitle', t)` and friends).
   Dependencies inside this library, consumed and never re-implemented:
   Component base + EventBinder, the options resolver (defaults <- data-* <-
   data-options <- ctor), Registry + facade, LayoutService (z scale, from
   ics-layout.js), WindowStackService (LIFO + ESC guard), Sanitizer. User
   notices go through the messager global IcsToastErr when it is present.
   ============================================================================= */
import { Component } from '../core/component.js';
import { Registry } from '../services/registry.js';
import { LayoutService } from './ics-layout.js';   /* the z-index scale; a side-effect-free import - that module registers nothing */
import { WindowStackService } from '../services/windowstack.js';
import { Sanitizer } from '../services/sanitizer.js';

const _g = globalThis;

/** user notice through the messager bridge; a page without it logs, never dialogs */
function _toastErr(m) {
  if (typeof _g.IcsToastErr === 'function') return _g.IcsToastErr(m);
  console.error('[ics] ' + m);
}

/* =============================================================================
   1. THE WIDGET
   ============================================================================= */

/** A source div removed from the document by page code (`$('#win').remove()`
    before a re-create is a common pattern) leaves its wrapper + mask behind.
    Every mount sweeps those orphans away first, so they never accumulate. */
function _sweepOrphans() {
  if (typeof document === 'undefined' || !document.body || !document.body.querySelectorAll) return 0;
  let n = 0;
  for (const wrap of Array.from(document.body.querySelectorAll('.ics-window'))) {
    if (wrap.querySelector && wrap.querySelector('.ics-window__bodysrc')) continue;   // source still inside = live
    const id = wrap.getAttribute && wrap.getAttribute('data-ics-owner');
    if (id) WindowStackService.pop(id);
    if (wrap._icsMask && wrap._icsMask.parentNode) wrap._icsMask.parentNode.removeChild(wrap._icsMask);
    if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    n++;
  }
  return n;
}

export class Window extends Component {
  static _seq = 0;                     // stack ids for id-less windows

  /** remove wrappers/masks whose source div page code has already removed; returns how many */
  static sweepOrphans() { return _sweepOrphans(); }

  /** options: static defaults <- data-* attributes <- data-options <- ctor argument.
      The title ATTRIBUTE is a further source (consumed at mount so no native
      tooltip shows); a window with no title at all is titled 'New Window'.
      Keys a data-options string may carry beyond these (collapsible,
      minimizable, maximizable, onRestore, ...) are accepted and have no effect:
      the title bar carries the close button only. */
  static defaults = {
    title: null,                       // HTML title
    modal: false,                      // dimmer behind the window
    closed: false,                     // false = shown at mount
    closable: true,                    // the close button (false = no button; close by code / ESC)
    draggable: true, resizable: true, shadow: true,
    noheader: false, border: true,
    disabled: false,                   // true = open() is suppressed (an early return in open)
    width: null, height: null,         // px; else the source div's inline width/height ('auto' hugs content)
    left: null, top: null,             // px; both null = centered at open
    box: false,                        // true = a message box / toast: never a close-topmost target (stack note in windowstack.js)
    onOpen: null, onClose: null, onResize: null,
  };

  _init() {
    const el = this.el, o = this.options;
    // title: options > title attribute (consumed) > default
    const attrTitle = el.getAttribute && el.getAttribute('title');
    if (attrTitle != null) el.removeAttribute('title');
    if (o.title == null && attrTitle != null && attrTitle !== '') o.title = attrTitle;
    if (o.title == null) o.title = 'New Window';

    _sweepOrphans();                                               // wrappers whose source div is gone

    // wrapper - relocated to body
    const wrap = this.wrap = document.createElement('div');
    wrap.className = 'ics-window';
    wrap.style.display = 'none';
    wrap.setAttribute('data-ics-owner', el.id || '');
    // geometry moves to the wrapper; padding stays on the source div (= body padding)
    /* the AUTHORED inline geometry, kept verbatim so _destroy can put it back.
       Callers size a window through exactly this inline style, so a
       destroy/re-create cycle that dropped it would silently resize the window. */
    this._srcW = el.style.width;
    this._srcH = el.style.height;
    const sw = o.width != null ? o.width + 'px' : (el.style.width || null);
    const sh = o.height != null ? o.height + 'px' : (el.style.height || null);
    if (sw) { this._cssW = sw; el.style.width = ''; }
    if (sh && !/^auto$/i.test(sh)) { this._cssH = sh; el.style.height = ''; }
    else if (sh) el.style.height = '';

    if (!o.noheader) {
      const head = this._head = document.createElement('div');
      head.className = 'ics-window__header';
      const title = this._title = document.createElement('span');
      title.className = 'ics-window__title';
      Sanitizer.setContent(title, o.title);                        // safe by default; see setTitle()
      head.appendChild(title);
      if (o.closable) {
        const btn = this._btnClose = document.createElement('a');
        btn.href = 'javascript:void(0)';
        /* panel-tool-close is a reserved hook on the close chip so a shell
           stylesheet can reach it (a `.no-title-close .panel-tool-close` rule,
           for example); the component itself attaches no paint or behaviour to
           the name. */
        btn.className = 'ics-window__close panel-tool-close';
        btn.innerHTML = '<i class="ics-icon-close-cancel" aria-hidden="true"></i>';
        btn.title = 'Close';
        btn.setAttribute('aria-label', 'Close');
        btn.onclick = () => this.close();
        head.appendChild(btn);
      }
      wrap.appendChild(head);
      this._bindDrag(head);
    }

    el.parentNode.insertBefore(wrap, el);
    wrap.appendChild(el);
    el.classList.add('ics-window__bodysrc');
    /* THE BODY NEVER SIZES ITSELF: the frame is the wrapper and the source div is the BODY,
       inset by a margin. A page STYLESHEET rule on the source div's own id - `#winX { width:
       100% }`, `#winAdvSearch { width: 96% !important }` - would be measured against the
       frame's content box and spill the body past the frame's right edge (every field runs
       over the border). The inline geometry is already lifted onto the wrapper above; a
       stylesheet rule cannot be lifted, so it is OVERRIDDEN inline with !important (the only
       weight that beats an !important id rule). Restored verbatim by _destroy. */
    el.style.setProperty('width', 'auto', 'important');
    el.style.setProperty('height', 'auto', 'important');
    el.setAttribute('data-ics-mounted', '1');                      // releases the pre-mount hide rule
    if (!o.border) wrap.classList.add('ics-window--borderless');
    if (!o.shadow) wrap.classList.add('ics-window--noshadow');
    document.body.appendChild(wrap);

    this._mask = null;                                             // created on first modal open
    if (o.resizable) this._buildResizeHandles();

    this._open = false;
    if (!o.closed) this.open();
  }

  /* ---- public methods (all reachable through $.fn.window) ------------------------ */

  /** show + bring to front. First open pushes onto the stack (z from the push);
      re-open while open only re-raises. Sequence: mask -> window -> position ->
      arrival (fade + rise, CSS motion tokens; none under reduced motion) ->
      onOpen (fired once per open, after the window is laid out and visible). */
  open() {
    if (this.options.disabled) return this;
    const o = this.options, wrap = this.wrap;
    if (!this._stackId) this._stackId = (this.el && this.el.id) || ('icswin-' + (++Window._seq));
    const wasOpen = this._open;
    const z = wasOpen
      ? (LayoutService.nextWindowZ ? LayoutService.nextWindowZ() : 1000)
      : WindowStackService.push({ id: this._stackId, el: this.el, close: () => this.close(), box: o.box === true || o.box === 'true' });
    if (o.modal) {
      if (!this._mask) {
        this._mask = document.createElement('div');
        this._mask.className = 'ics-window__mask';
        document.body.appendChild(this._mask);
        wrap._icsMask = this._mask;                                // the sweep removes the pair together
      }
      this._sizeMask();
      this._mask.style.zIndex = String(z - 1);
      if (!wasOpen) this._mask.classList.add('ics-window__mask--pre');
      this._mask.style.display = 'block';
      if (!this._maskResize) {
        this._maskResize = () => this._sizeMask();
        this._events.add(window, 'resize', this._maskResize);      // mask re-stretches with the viewport
      }
    }
    wrap.style.zIndex = String(z);
    // a % width resolves to px at open (a later viewport change does not re-layout; center() is the manual fix)
    if (this._cssW) {
      wrap.style.width = /%$/.test(this._cssW)
        ? Math.round(document.documentElement.clientWidth * parseFloat(this._cssW) / 100) + 'px'
        : this._cssW;
    }
    if (this._cssH) wrap.style.height = this._cssH;
    if (!wasOpen) wrap.classList.add('ics-window--pre');            // arrival start state (transparent, raised)
    wrap.style.display = 'flex';                                   // title bar + body as a column (body scrolls inside the frame)
    this._open = true;
    if (o.left != null && typeof o.left === 'number') wrap.style.left = o.left + 'px';
    if (o.top != null && typeof o.top === 'number') wrap.style.top = o.top + 'px';
    if (!(typeof o.left === 'number' || typeof o.top === 'number') && !this._userMoved) this.center();
    if (!wasOpen) {
      void wrap.offsetWidth;                                       // commit the start state, then let the transition run
      wrap.classList.remove('ics-window--pre');
      if (this._mask) this._mask.classList.remove('ics-window__mask--pre');
      this._fitContent();                                          // content laid out while hidden re-measures now
      if (typeof o.onOpen === 'function') o.onOpen.call(this.el);
    }
    return this;
  }

  /** Content that measures itself (panels, layouts, tabs, grids, fluid widgets)
      was initialised while the source div was hidden and may have taken a 0px
      size. After the window is visible - and after every size change - each such
      widget is told to re-fit by the `_resize` signal its own container would
      send it, forced. No-op where no such widget exists. */
  _fitContent() {
    const $ = _g.jQuery || _g.$;
    if (!$ || !$.fn || typeof $.fn.panel !== 'function' || !this.el) return;
    try {
      $(this.el).find('div.panel:visible,div.accordion:visible,div.tabs-container:visible,div.layout:visible')
        .each(function () { $(this).triggerHandler('_resize', [true]); });
    } catch (e) { console.error('[ics] window content re-fit failed', e); }
  }

  /** hide + pop + onClose once; idempotent, never throws */
  close() {
    if (!this._open) return this;
    this._open = false;
    if (this._stackId) WindowStackService.pop(this._stackId);
    this.wrap.style.display = 'none';
    if (this._mask) this._mask.style.display = 'none';
    if (typeof this.options.onClose === 'function') this.options.onClose.call(this.el);
    return this;
  }

  /** center in the viewport (page-scroll aware) */
  center() {
    const wrap = this.wrap;
    const vw = document.documentElement.clientWidth, vh = document.documentElement.clientHeight;
    const r = wrap.getBoundingClientRect();
    wrap.style.left = Math.max(0, window.scrollX + (vw - r.width) / 2) + 'px';
    wrap.style.top = Math.max(0, window.scrollY + (vh - r.height) / 2) + 'px';
    this._userMoved = false;
    return this;
  }

  /** Title; undefined/null = no-op (keeps the current title).
      SAFE BY DEFAULT: a plain string is set as TEXT, so a title carrying server
      data - a customer, style or session name a user typed - cannot inject
      markup. A title that really is markup opts in explicitly and greppably with
      `Sanitizer.html('<i class="ics-icon-save"></i> Caption')`. */
  setTitle(s) {
    if (s === undefined || s === null) return this;
    if (this._title) Sanitizer.setContent(this._title, s);
    this.options.title = s;
    return this;
  }

  /** resize({width, height}) in px (either may be omitted); fires onResize */
  resize(size) {
    const wrap = this.wrap;
    if (size && size.width != null) wrap.style.width = size.width + 'px';
    if (size && size.height != null) wrap.style.height = size.height + 'px';
    this._fireResize();
    return this;
  }

  _fireResize() {
    this._fitContent();
    /* REMEMBER THE SIZE, the way a drag already remembers the position. open()
       re-asserts _cssW/_cssH every time, so without this write-back a user who
       resized the window would get the original size back on the next open while
       their moved position survived - two halves of the same gesture behaving
       differently. This is the single seam: resize() and the eight drag handles
       are the only callers, and open() calls _fitContent() directly, never this,
       so re-asserting on open cannot clobber the remembered value. */
    const w = this.wrap.style.width, h = this.wrap.style.height;
    if (w) this._cssW = w;
    if (h) this._cssH = h;
    if (typeof this.options.onResize === 'function') {
      const r = this.wrap.getBoundingClientRect();
      this.options.onResize.call(this.el, Math.round(r.width), Math.round(r.height));
    }
  }

  _sizeMask() {
    if (!this._mask) return;
    const w = Math.max(document.documentElement.scrollWidth, document.documentElement.clientWidth);
    const h = Math.max(document.documentElement.scrollHeight, document.documentElement.clientHeight);
    this._mask.style.width = w + 'px';
    this._mask.style.height = h + 'px';
  }

  /* ---- pointer gestures ------------------------------------------------------------ */

  /** one document-level move/up pair per gesture, released on pointerup */
  _trackPointer(onMove) {
    const move = (ev) => onMove(ev);
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
  }

  /** drag by the header; the header can never leave the top of the viewport */
  _bindDrag(head) {
    this._events.add(head, 'pointerdown', (e) => {
      if (!this.options.draggable || e.target.closest('.ics-window__close')) return;
      e.preventDefault();
      const wrap = this.wrap;
      const sx = e.clientX, sy = e.clientY;
      const ox = parseFloat(wrap.style.left) || 0, oy = parseFloat(wrap.style.top) || 0;
      this._trackPointer((ev) => {
        wrap.style.left = (ox + ev.clientX - sx) + 'px';
        wrap.style.top = Math.max(0, oy + ev.clientY - sy) + 'px';
        this._userMoved = true;
      });
    });
  }

  /** eight edge/corner handles; min 80x40; onResize fires continuously */
  _buildResizeHandles() {
    const dirs = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
    for (const d of dirs) {
      const h = document.createElement('span');
      h.className = 'ics-window__rs ics-window__rs--' + d;
      this.wrap.appendChild(h);
      this._events.add(h, 'pointerdown', (e) => {
        if (!this.options.resizable) return;
        e.preventDefault(); e.stopPropagation();
        const wrap = this.wrap, r = wrap.getBoundingClientRect();
        const sx = e.clientX, sy = e.clientY;
        const start = { l: parseFloat(wrap.style.left) || 0, t: parseFloat(wrap.style.top) || 0, w: r.width, h: r.height };
        this._trackPointer((ev) => {
          const dx = ev.clientX - sx, dy = ev.clientY - sy;
          let { l, t, w, h: hh } = start;
          if (d.includes('e')) w = Math.max(80, start.w + dx);
          if (d.includes('s')) hh = Math.max(40, start.h + dy);
          if (d.includes('w')) { w = Math.max(80, start.w - dx); l = start.l + (start.w - w); }
          if (d.includes('n')) { hh = Math.max(40, start.h - dy); t = start.t + (start.h - hh); }
          wrap.style.left = l + 'px'; wrap.style.top = t + 'px';
          wrap.style.width = w + 'px'; wrap.style.height = hh + 'px';
          this._fireResize();
        });
      });
    }
  }

  /** option(name, value) re-config: title re-renders; the on* callbacks are read live */
  _optionChanged(name, value) {
    if (name === 'title') { this.setTitle(value); return; }
  }

  /** teardown: off the stack, mask + wrapper removed, source div returned to
      where the wrapper stood; a same-id re-create never leaves anything behind */
  _destroy() {
    if (this._stackId) { WindowStackService.pop(this._stackId); this._stackId = null; }
    if (this._mask && this._mask.parentNode) this._mask.parentNode.removeChild(this._mask);
    this._mask = null;
    if (this.wrap && this.wrap.parentNode) {
      this.wrap.parentNode.insertBefore(this.el, this.wrap);
      this.wrap.remove();
    }
    this.el.classList.remove('ics-window__bodysrc');
    /* put the authored inline geometry back - _init moved it onto the wrapper and
       blanked it here, so leaving it blank would lose the size on a re-create. */
    this.el.style.width = this._srcW || '';
    this.el.style.height = this._srcH || '';
    this.el.removeAttribute('data-ics-mounted');
  }
}

/* the facade mints $.fn.window from claimName; facade:false keeps $.fn.icsWindow
   for the application-level call defined below (its close semantics differ) */
Registry.add('Window', Window, {
  selector: '[data-ics="window"]',                            // the one authored form of a window
  facade: false,
  claimName: 'window',
});

/* =============================================================================
   2. CLOSE THE TOPMOST WINDOW
   ============================================================================= */

/**
 * Close the topmost window on the stack (message boxes / toasts are walked past -
 * they are never a target of this call). Returns true when one was closed.
 * `stack` (optional) is an external LIFO of `{ WinId }` entries: it is consulted
 * ONLY when the service stack holds no closable window - then its last entry is
 * popped and `$('#WinId').window('close')` runs, under the same one-ESC-one-window guard.
 */
export function icsWindowCloseTop(stack) {
  if (WindowStackService.closableDepth > 0) return WindowStackService.closeTop();

  if (Array.isArray(stack) && stack.length > 0) {
    if (WindowStackService.escBlocked()) return false;
    WindowStackService.noteEscConsumed();
    const sWinId = stack[stack.length - 1].WinId || '';
    stack.pop();
    const $ = _g.jQuery || _g.$;
    if (sWinId !== '' && $ && $.fn && typeof $.fn.window === 'function') {
      $('#' + sWinId).window('close');
      return true;
    }
    return false;
  }
  return false;                                                     // nothing open = silent no-op
}

/** page-global alias: IcsWindowClose(oActiveWindows) */
export function IcsWindowClose(oActiveWindows) {
  return icsWindowCloseTop(oActiveWindows);
}

/* =============================================================================
   3. WINDOW-ADJACENT PAGE GLOBALS (attached with the family)
   ============================================================================= */

/**
 * A NO-OP that exists for its name: page code calls it and discards the result.
 * There is nothing for it to do, so the body is empty; the name stays callable.
 */
export function UnselectAllRowsOfATable() {}

/** POST hidden fields to a URL in a new tab (documents / reports): a form is
    created, submitted and removed. `objs` = [{ key, data }, ...] */
export function OpenWindowWithPost(url, objs) {
  const form = document.createElement('form');
  form.setAttribute('method', 'post');
  form.setAttribute('action', url);
  form.setAttribute('target', '_blank');
  /* a plain array walk: nothing here needs jQuery, so this runs unchanged on a
     shell without it (every jQuery use in this module is behind `if ($)`) */
  if (objs != null && objs.length > 0) {
    for (const obj of objs) {
      if (obj && Object.prototype.hasOwnProperty.call(obj, 'key')
              && Object.prototype.hasOwnProperty.call(obj, 'data')) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = obj['key'];
        input.value = obj['data'];
        form.appendChild(input);
      }
    }
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}

/* =============================================================================
   4. $.fn.icsWindow(action, title) - the application-level open / close
   ============================================================================= */

/** $.trim when jQuery provides it, String.trim otherwise */
function _trim($, v) {
  if ($ && typeof $.trim === 'function') return $.trim(v);
  return v == null ? '' : String(v).trim();
}

/**
 * Attach $.fn.icsWindow. Contract:
 *   $('#win').icsWindow('open'[, title])  opens the receiver through $.fn.window
 *                                          and applies the title (undefined keeps
 *                                          the current one)
 *   $(sel).icsWindow('close')              closes the topmost OPEN window among the
 *                                          receiver's elements ($('#win') = that
 *                                          window, wherever it sits on the stack);
 *                                          a receiver naming no open window
 *                                          ($('div'), $(this) of a button, a closed
 *                                          id) closes the topmost window overall.
 *                                          Message boxes / toasts are never the
 *                                          target; one window per call; a close
 *                                          during an Escape dispatch that already
 *                                          closed one is a no-op (one ESC = one).
 * `action` must be the string 'open' or 'close' (case-insensitive); anything
 * else, or a non-string, raises a user notice and does nothing. Not chainable.
 */
export function attachWindowFamily($) {
  if (!$ || !$.fn) return false;

  $.fn.icsWindow = function (action, title) {
    /* type-check the VALUE, not its length: `icsWindow()` / `icsWindow(null)` must
       raise the documented notice, never a TypeError from `action.length` - an
       uncaught throw takes the caller's remaining statements with it, which is
       the failure this notice exists to avoid. */
    if (typeof action !== 'string' || !_trim($, action)) {
      _toastErr("icsWindow parameter value not found. Use 'open' or 'close'");
      return;
    }
    if (action.toLowerCase() != 'open' && action.toLowerCase() != 'close') {
      _toastErr("icsWindow parameter value must be 'open' or 'close'.");
      return;
    }

    if (action.toLowerCase() == 'open') {
      $(this).window('open');
      $(this).window('setTitle', title);
      /* an ics Window registers itself on the stack inside open(); a window served
         by another $.fn.window implementation is registered here so it takes part
         in close-topmost like any other */
      const el = this[0] || this;
      if (!Window.get(el)) {
        const id = $(this).attr('id');
        WindowStackService.push({ id: id, el: el, close: () => $('#' + id).window('close') });
      }
    } else {
      /* PROGRAMMATIC close (no esc flag): never blocked by the one-ESC guard, so a
         close called from inside an Esc-driven callback closes ITS window instead of
         being refused and falling through to close-topmost. */
      /* the receiver names the window: close the topmost open one among its elements
         (an entry's `el` is the source div, for own windows and registered foreign ones
         alike); nothing named/open -> the topmost window overall */
      const named = new Set();
      this.each(function () { named.add(this); });
      if (!WindowStackService.closeTopOf((e) => named.has(e.el))) icsWindowCloseTop(_g.oActiveWindows);
    }
  };

  return true;
}

/* =============================================================================
   5. $.fn.panel on a WINDOW - "a window is a panel" idiom
   =============================================================================
   Page code addresses a window through the panel API as well: `$('#win').panel('setTitle', t)`,
   `$('#win').panel({ title })`, `$('#win').panel('resize', …)`, `panel('options')`,
   `panel('panel')`. The source div of an own window is not a panel, so such a call
   would reach a panel implementation that throws on it - and an uncaught throw takes
   the caller's remaining statements with it (a re-title in a footer hook can silently
   cost a dialog its Ok/Close bindings). installPanelDispatch($) wraps whatever holds
   $.fn.panel: a receiver that is an own window (mounted, or declared with the own
   name) is served by the window - the panel vocabulary is translated - and every
   other receiver goes to the implementation installed before us, untouched. */
const PANEL_TO_WINDOW = {
  setTitle: 'setTitle', resize: 'resize', open: 'open', close: 'close', center: 'center',
  destroy: 'destroy', options: 'options',
};
const PANEL_NOOP = ['refresh', 'collapse', 'expand', 'maximize', 'restore', 'move', 'doLayout', 'clear'];

/** true when el is an own window: mounted, or declared with the own name */
function _isOwnWindow(el) {
  if (!el || el.nodeType !== 1) return false;
  if (Window.get(el)) return true;
  return !!(el.matches && el.matches('[data-ics="window"]'));
}

/**
 * Wrap $.fn.panel so calls whose receiver is an own window reach the window
 * component; everything else keeps the original panel implementation. Idempotent.
 * @returns {boolean} installed (or already installed); false without jQuery
 */
export function installPanelDispatch($) {
  if (!$ || !$.fn) return false;
  const cur = $.fn.panel;
  if (typeof cur === 'function' && cur._icsPanelDispatch) return true;
  const original = typeof cur === 'function' ? cur : null;
  const dispatch = function (arg, ...rest) {
    if (!this.length || !_isOwnWindow(this[0])) {
      if (original) return original.apply(this, arguments);
      console.warn('[ics] $.fn.panel: no panel implementation on this page', this[0]);
      return this;
    }
    const $win = $(this[0]);
    if (arg && typeof arg === 'object') return $win.window(arg);              // re-configure (title, width, …)
    if (typeof arg !== 'string') return this;
    const inst = Window.get(this[0]) || ($win.window({}) && Window.get(this[0]));  // mount-on-demand for the own name
    if (!inst) return this;
    if (arg === 'panel') return $(inst.wrap);                                     // the outer box = our wrapper
    if (arg === 'header') return $(inst._head);
    if (arg === 'body') return $win;                                              // the source div IS the body
    if (PANEL_TO_WINDOW[arg]) return $win.window(PANEL_TO_WINDOW[arg], ...rest);
    if (PANEL_NOOP.indexOf(arg) >= 0) return this;                                // panel-only geometry verbs: nothing to do on a window
    console.warn("[ics] $.fn.panel on a window: unknown method '" + arg + "'", this[0]);
    return this;
  };
  if (original) Object.assign(dispatch, original);        // defaults / methods / parseOptions carried
  dispatch._icsPanelDispatch = true;
  dispatch._icsForeign = original;
  $.fn.panel = dispatch;
  return true;
}

/** the page globals of the window family */
export const WINDOW_GLOBALS = { IcsWindowClose, icsWindowCloseTop, UnselectAllRowsOfATable, OpenWindowWithPost };

/** attach the page globals; returns how many names were set */
export function attachWindowGlobals(g) {
  const t = g || globalThis;
  let n = 0;
  for (const k of Object.keys(WINDOW_GLOBALS)) { t[k] = WINDOW_GLOBALS[k]; n++; }
  return n;
}
